import { useState, useEffect, useCallback } from 'react';
import { CreateTemplateFormData, initialFormData } from './types';

const STORAGE_KEY = 'create_class_template_form';

interface StoredState {
  currentStep: number;
  formData: CreateTemplateFormData;
}

export function useCreateTemplateForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<CreateTemplateFormData>(initialFormData);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed: StoredState = JSON.parse(saved);
        setCurrentStep(parsed.currentStep || 1);
        setFormData({ ...initialFormData, ...parsed.formData });
      }
    } catch (e) {
      console.error('Failed to load form data from localStorage:', e);
    }
    setIsInitialized(true);
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    if (!isInitialized) return;
    
    try {
      const state: StoredState = { currentStep, formData };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save form data to localStorage:', e);
    }
  }, [currentStep, formData, isInitialized]);

  const updateFormData = useCallback((updates: Partial<CreateTemplateFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const clearStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setFormData(initialFormData);
    setCurrentStep(1);
  }, []);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 5) {
      setCurrentStep(step);
    }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, 5));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  return {
    currentStep,
    setCurrentStep: goToStep,
    formData,
    updateFormData,
    clearStorage,
    nextStep,
    prevStep,
    isInitialized,
  };
}
